/**
 * Filesystem setup for the local-CC channel plugin.
 *
 * Idempotent: safe to call multiple times. Writes:
 *   ~/.synkro/cc_sessions/synkro-channel.ts   (the Bun MCP plugin)
 *   ~/.synkro/cc_sessions/package.json        (deps for the plugin)
 *   ~/.synkro/cc_sessions/.claude/settings.json   (fastMode:true scoped to this cwd)
 *
 * Then patches ~/.claude.json to register the plugin under mcpServers, and runs
 * `bun install` in the session dir so @modelcontextprotocol/sdk is on disk.
 */

import { existsSync, mkdirSync, writeFileSync, readFileSync, chmodSync, copyFileSync, renameSync, unlinkSync, openSync, fsyncSync, closeSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';
import { spawnSync } from 'node:child_process';

export const CLAUDE_JSON_BACKUP_PATH = join(homedir(), '.claude.json.synkro-bak');

export const SESSION_DIR = join(homedir(), '.synkro', 'cc_sessions');
export const PLUGIN_PATH = join(SESSION_DIR, 'synkro-channel.ts');
export const PLUGIN_PKG_PATH = join(SESSION_DIR, 'package.json');
export const PLUGIN_SETTINGS_DIR = join(SESSION_DIR, '.claude');
export const PLUGIN_SETTINGS_PATH = join(PLUGIN_SETTINGS_DIR, 'settings.json');
export const PROJECT_MCP_PATH = join(SESSION_DIR, '.mcp.json');
export const CLAUDE_JSON_PATH = join(homedir(), '.claude.json');
export const RUN_SCRIPT_PATH = join(SESSION_DIR, 'run-claude.sh');
export const TMUX_SESSION_NAME = 'synkro-local-cc';

// Channel 1 (general A) — pueue task → claude listening on this internal port.
// The MCP server's dispatcher (8929) round-robins between Channel 1 and 3.
export const CHANNEL_1_PORT = 8941;

export const SESSION_DIR_2 = join(homedir(), '.synkro', 'cc_sessions_2');
export const PLUGIN_PATH_2 = join(SESSION_DIR_2, 'synkro-channel.ts');
export const PLUGIN_PKG_PATH_2 = join(SESSION_DIR_2, 'package.json');
export const PLUGIN_SETTINGS_DIR_2 = join(SESSION_DIR_2, '.claude');
export const PLUGIN_SETTINGS_PATH_2 = join(PLUGIN_SETTINGS_DIR_2, 'settings.json');
export const PROJECT_MCP_PATH_2 = join(SESSION_DIR_2, '.mcp.json');
export const RUN_SCRIPT_PATH_2 = join(SESSION_DIR_2, 'run-claude.sh');
export const TMUX_SESSION_NAME_2 = 'synkro-local-cc-2';
// CWE worker A. Dispatcher on 8930 round-robins Channel 2 and 4.
export const CHANNEL_2_PORT = 8951;

export const SESSION_DIR_3 = join(homedir(), '.synkro', 'cc_sessions_3');
export const PLUGIN_PATH_3 = join(SESSION_DIR_3, 'synkro-channel.ts');
export const PLUGIN_PKG_PATH_3 = join(SESSION_DIR_3, 'package.json');
export const PLUGIN_SETTINGS_DIR_3 = join(SESSION_DIR_3, '.claude');
export const PLUGIN_SETTINGS_PATH_3 = join(PLUGIN_SETTINGS_DIR_3, 'settings.json');
export const PROJECT_MCP_PATH_3 = join(SESSION_DIR_3, '.mcp.json');
export const RUN_SCRIPT_PATH_3 = join(SESSION_DIR_3, 'run-claude.sh');
export const TMUX_SESSION_NAME_3 = 'synkro-local-cc-3';
export const CHANNEL_3_PORT = 8942; // general worker B

export const SESSION_DIR_4 = join(homedir(), '.synkro', 'cc_sessions_4');
export const PLUGIN_PATH_4 = join(SESSION_DIR_4, 'synkro-channel.ts');
export const PLUGIN_PKG_PATH_4 = join(SESSION_DIR_4, 'package.json');
export const PLUGIN_SETTINGS_DIR_4 = join(SESSION_DIR_4, '.claude');
export const PLUGIN_SETTINGS_PATH_4 = join(PLUGIN_SETTINGS_DIR_4, 'settings.json');
export const PROJECT_MCP_PATH_4 = join(SESSION_DIR_4, '.mcp.json');
export const RUN_SCRIPT_PATH_4 = join(SESSION_DIR_4, 'run-claude.sh');
export const TMUX_SESSION_NAME_4 = 'synkro-local-cc-4';
export const CHANNEL_4_PORT = 8952; // CWE worker B

/**
 * Bash wrapper that owns the tmux session hosting `claude`. Pueue runs this
 * script as its task. The script:
 *   1. Kills any prior tmux session by the same name (idempotent restart).
 *   2. Starts a detached tmux session running claude. tmux gives claude a
 *      real pty, so it stays in interactive mode (vs. dropping into --print).
 *   3. Blocks on tmux's lifetime so pueue's task duration mirrors claude's.
 *      `synkro local-cc stop` kills the tmux session, which unblocks the
 *      poll and lets pueue mark the task done.
 *
 * Dismissal of startup dialogs (workspace trust / dev-channels /
 * MCP consent) is owned by the TS-side waitForChannelReady, which polls
 * the channel TCP port and injects \`tmux send-keys ... Enter\` each tick
 * the probe fails. That's adaptive (no fixed window) and self-terminating
 * (stops the moment the port binds).
 */
const RUN_SCRIPT_SOURCE = `#!/usr/bin/env bash
# Auto-generated by \`synkro install\`. Do not edit.
set -uo pipefail

SESSION=${TMUX_SESSION_NAME}
LOG="$HOME/.synkro/cc_sessions/run-claude.log"

log() { echo "[$(date '+%H:%M:%S')] $*" >> "$LOG"; echo "$*"; }

# Pre-flight checks
if ! command -v claude >/dev/null 2>&1; then
  log "ERROR: claude CLI not found on PATH. Install Claude Code first."
  exit 1
fi

if ! command -v tmux >/dev/null 2>&1; then
  log "ERROR: tmux not found on PATH."
  exit 1
fi

# Check claude is authenticated
if ! claude --version >/dev/null 2>&1; then
  log "ERROR: claude --version failed. Is Claude Code installed correctly?"
  exit 1
fi

log "Starting local-CC session..."
log "claude version: $(claude --version 2>&1 | head -1)"

# Kill any previous session so restarts come up clean.
tmux kill-session -t "=$SESSION" 2>/dev/null || true

# Start claude inside a detached tmux session so it has a real pty.
# Redirect stderr to the log so we can see why it dies.
tmux new-session -d -s "$SESSION" \\
  "SYNKRO_CHANNEL_PORT=${CHANNEL_1_PORT} CLAUDE_CODE_DISABLE_THINKING=1 claude --dangerously-load-development-channels server:synkro-local --dangerously-skip-permissions --setting-sources project,local --model claude-sonnet-5 2>>$LOG; echo 'claude exited with code '\$'?' >> $LOG"

# Claude's --dangerously-load-development-channels shows a confirmation
# prompt: option 1 = "I am using this for local development" (accept),
# option 2 = "Exit". Auto-accept by sending '1' + Enter.
sleep 3
if tmux has-session -t "=$SESSION" 2>/dev/null; then
  tmux send-keys -t "$SESSION" '1' 2>/dev/null || true
  sleep 1
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  sleep 1
  # Additional Enter for any follow-up prompts (workspace trust, MCP consent)
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  log "Sent auto-accept keys to claude session."
fi

sleep 2
if ! tmux has-session -t "$SESSION" 2>/dev/null; then
  log "ERROR: tmux session died immediately. Check $LOG for details."
  log "Try running claude manually to verify auth: claude --print 'say ok'"
  exit 1
fi

log "tmux session started successfully."

# Block on the tmux session so pueue's task lifetime tracks claude's.
while tmux has-session -t "=$SESSION" 2>/dev/null; do
  sleep 5
done

log "tmux session ended."
`;

const RUN_SCRIPT_SOURCE_2 = `#!/usr/bin/env bash
# Auto-generated by \`synkro install\`. Channel 2 (CWE scan, port ${CHANNEL_2_PORT}).
set -uo pipefail

SESSION=${TMUX_SESSION_NAME_2}
LOG="$HOME/.synkro/cc_sessions_2/run-claude.log"

log() { echo "[$(date '+%H:%M:%S')] $*" >> "$LOG"; echo "$*"; }

if ! command -v claude >/dev/null 2>&1; then
  log "ERROR: claude CLI not found on PATH."
  exit 1
fi

if ! command -v tmux >/dev/null 2>&1; then
  log "ERROR: tmux not found on PATH."
  exit 1
fi

if ! claude --version >/dev/null 2>&1; then
  log "ERROR: claude --version failed."
  exit 1
fi

log "Starting local-CC channel 2 (port ${CHANNEL_2_PORT})..."
log "claude version: $(claude --version 2>&1 | head -1)"

tmux kill-session -t "=$SESSION" 2>/dev/null || true

tmux new-session -d -s "$SESSION" \\
  "SYNKRO_CHANNEL_PORT=${CHANNEL_2_PORT} CLAUDE_CODE_DISABLE_THINKING=1 claude --dangerously-load-development-channels server:synkro-local --dangerously-skip-permissions --setting-sources project,local --model claude-sonnet-5 2>>$LOG; echo 'claude exited with code '\$'?' >> $LOG"

sleep 3
if tmux has-session -t "=$SESSION" 2>/dev/null; then
  tmux send-keys -t "$SESSION" '1' 2>/dev/null || true
  sleep 1
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  sleep 1
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  log "Sent auto-accept keys to channel 2 session."
fi

sleep 2
if ! tmux has-session -t "$SESSION" 2>/dev/null; then
  log "ERROR: tmux session died immediately. Check $LOG for details."
  exit 1
fi

log "tmux session started successfully (port ${CHANNEL_2_PORT})."

while tmux has-session -t "=$SESSION" 2>/dev/null; do
  sleep 5
done

log "tmux session ended."
`;

// General worker B (port CHANNEL_3_PORT) — dispatcher pairs this with cc_sessions on 8929.
const RUN_SCRIPT_SOURCE_3 = `#!/usr/bin/env bash
# Auto-generated by \`synkro install\`. General worker B (port ${CHANNEL_3_PORT}).
set -uo pipefail

SESSION=${TMUX_SESSION_NAME_3}
LOG="$HOME/.synkro/cc_sessions_3/run-claude.log"

log() { echo "[$(date '+%H:%M:%S')] $*" >> "$LOG"; echo "$*"; }

if ! command -v claude >/dev/null 2>&1; then
  log "ERROR: claude CLI not found on PATH."
  exit 1
fi

if ! command -v tmux >/dev/null 2>&1; then
  log "ERROR: tmux not found on PATH."
  exit 1
fi

if ! claude --version >/dev/null 2>&1; then
  log "ERROR: claude --version failed."
  exit 1
fi

log "Starting local-CC general worker B (port ${CHANNEL_3_PORT})..."
log "claude version: $(claude --version 2>&1 | head -1)"

tmux kill-session -t "=$SESSION" 2>/dev/null || true

tmux new-session -d -s "$SESSION" \\
  "SYNKRO_CHANNEL_PORT=${CHANNEL_3_PORT} CLAUDE_CODE_DISABLE_THINKING=1 claude --dangerously-load-development-channels server:synkro-local --dangerously-skip-permissions --setting-sources project,local --model claude-sonnet-5 2>>$LOG; echo 'claude exited with code '\$'?' >> $LOG"

sleep 3
if tmux has-session -t "=$SESSION" 2>/dev/null; then
  tmux send-keys -t "$SESSION" '1' 2>/dev/null || true
  sleep 1
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  sleep 1
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  log "Sent auto-accept keys to general worker B session."
fi

sleep 2
if ! tmux has-session -t "$SESSION" 2>/dev/null; then
  log "ERROR: tmux session died immediately. Check $LOG for details."
  exit 1
fi

log "tmux session started successfully (port ${CHANNEL_3_PORT})."

while tmux has-session -t "=$SESSION" 2>/dev/null; do
  sleep 5
done

log "tmux session ended."
`;

// CWE worker B (port CHANNEL_4_PORT) — dispatcher pairs this with cc_sessions_2 on 8930.
const RUN_SCRIPT_SOURCE_4 = `#!/usr/bin/env bash
# Auto-generated by \`synkro install\`. CWE worker B (port ${CHANNEL_4_PORT}).
set -uo pipefail

SESSION=${TMUX_SESSION_NAME_4}
LOG="$HOME/.synkro/cc_sessions_4/run-claude.log"

log() { echo "[$(date '+%H:%M:%S')] $*" >> "$LOG"; echo "$*"; }

if ! command -v claude >/dev/null 2>&1; then
  log "ERROR: claude CLI not found on PATH."
  exit 1
fi

if ! command -v tmux >/dev/null 2>&1; then
  log "ERROR: tmux not found on PATH."
  exit 1
fi

if ! claude --version >/dev/null 2>&1; then
  log "ERROR: claude --version failed."
  exit 1
fi

log "Starting local-CC CWE worker B (port ${CHANNEL_4_PORT})..."
log "claude version: $(claude --version 2>&1 | head -1)"

tmux kill-session -t "=$SESSION" 2>/dev/null || true

tmux new-session -d -s "$SESSION" \\
  "SYNKRO_CHANNEL_PORT=${CHANNEL_4_PORT} CLAUDE_CODE_DISABLE_THINKING=1 claude --dangerously-load-development-channels server:synkro-local --dangerously-skip-permissions --setting-sources project,local --model claude-sonnet-5 2>>$LOG; echo 'claude exited with code '\$'?' >> $LOG"

sleep 3
if tmux has-session -t "=$SESSION" 2>/dev/null; then
  tmux send-keys -t "$SESSION" '1' 2>/dev/null || true
  sleep 1
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  sleep 1
  tmux send-keys -t "$SESSION" Enter 2>/dev/null || true
  log "Sent auto-accept keys to CWE worker B session."
fi

sleep 2
if ! tmux has-session -t "$SESSION" 2>/dev/null; then
  log "ERROR: tmux session died immediately. Check $LOG for details."
  exit 1
fi

log "tmux session started successfully (port ${CHANNEL_4_PORT})."

while tmux has-session -t "=$SESSION" 2>/dev/null; do
  sleep 5
done

log "tmux session ended."
`;

const MCP_SERVER_NAME = 'synkro-local';

const PLUGIN_PACKAGE_JSON = JSON.stringify(
  {
    name: 'synkro-local-channel',
    private: true,
    version: '0.1.0',
    type: 'module',
    dependencies: {
      '@modelcontextprotocol/sdk': '^1.0.0',
    },
  },
  null,
  2,
) + '\n';

export class LocalCCInstallError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'LocalCCInstallError';
  }
}

interface ChannelInstallSlot {
  sessionDir: string;
  pluginPath: string;
  pluginPkgPath: string;
  pluginSettingsDir: string;
  pluginSettingsPath: string;
  projectMcpPath: string;
  runScriptPath: string;
  runScriptSource: string;
}

const CHANNELS: readonly ChannelInstallSlot[] = [
  { sessionDir: SESSION_DIR,   pluginPath: PLUGIN_PATH,   pluginPkgPath: PLUGIN_PKG_PATH,   pluginSettingsDir: PLUGIN_SETTINGS_DIR,   pluginSettingsPath: PLUGIN_SETTINGS_PATH,   projectMcpPath: PROJECT_MCP_PATH,   runScriptPath: RUN_SCRIPT_PATH,   runScriptSource: RUN_SCRIPT_SOURCE   },
  { sessionDir: SESSION_DIR_2, pluginPath: PLUGIN_PATH_2, pluginPkgPath: PLUGIN_PKG_PATH_2, pluginSettingsDir: PLUGIN_SETTINGS_DIR_2, pluginSettingsPath: PLUGIN_SETTINGS_PATH_2, projectMcpPath: PROJECT_MCP_PATH_2, runScriptPath: RUN_SCRIPT_PATH_2, runScriptSource: RUN_SCRIPT_SOURCE_2 },
  { sessionDir: SESSION_DIR_3, pluginPath: PLUGIN_PATH_3, pluginPkgPath: PLUGIN_PKG_PATH_3, pluginSettingsDir: PLUGIN_SETTINGS_DIR_3, pluginSettingsPath: PLUGIN_SETTINGS_PATH_3, projectMcpPath: PROJECT_MCP_PATH_3, runScriptPath: RUN_SCRIPT_PATH_3, runScriptSource: RUN_SCRIPT_SOURCE_3 },
  { sessionDir: SESSION_DIR_4, pluginPath: PLUGIN_PATH_4, pluginPkgPath: PLUGIN_PKG_PATH_4, pluginSettingsDir: PLUGIN_SETTINGS_DIR_4, pluginSettingsPath: PLUGIN_SETTINGS_PATH_4, projectMcpPath: PROJECT_MCP_PATH_4, runScriptPath: RUN_SCRIPT_PATH_4, runScriptSource: RUN_SCRIPT_SOURCE_4 },
];

function writePluginFiles(): void {
  for (const c of CHANNELS) {
    mkdirSync(c.sessionDir, { recursive: true });
    mkdirSync(c.pluginSettingsDir, { recursive: true });
    // Channel plugin source lives in the Docker image only (IP protection).
    // Workers inside the container use /app/channel-plugin.ts directly.
    writeFileSync(c.pluginPkgPath, PLUGIN_PACKAGE_JSON, 'utf-8');
    writeFileSync(
      c.pluginSettingsPath,
      JSON.stringify({
        fastMode: true,
        enabledMcpjsonServers: ['synkro-local'],
        // Extended thinking is pure latency tax on a structured verdict grade and
        // emits reasoning the verdict parser isn't tuned for. effortLevel only
        // SOFTENS it; CLAUDE_CODE_DISABLE_THINKING=1 is the authoritative off
        // switch. Mirror the cloud pool (pool-config.sh) so local + cloud graders
        // behave identically.
        effortLevel: 'low',
        env: { CLAUDE_CODE_DISABLE_THINKING: '1' },
      }, null, 2) + '\n',
      'utf-8',
    );
    writeFileSync(c.runScriptPath, c.runScriptSource, 'utf-8');
    chmodSync(c.runScriptPath, 0o755);
  }
}

function runBunInstall(): void {
  for (const c of CHANNELS) {
    const r = spawnSync('bun', ['install', '--silent'], {
      cwd: c.sessionDir,
      encoding: 'utf-8',
      timeout: 120_000,
    });
    if (r.status !== 0) {
      throw new LocalCCInstallError(
        `bun install failed in ${c.sessionDir}: ${r.stderr || r.stdout || 'unknown'}`,
      );
    }
  }
}

interface ClaudeJson {
  mcpServers?: Record<string, { command: string; args?: string[]; env?: Record<string, string> }>;
  projects?: Record<string, Record<string, unknown>>;
  [k: string]: unknown;
}

/**
 * Safely apply `mutator` to ~/.claude.json with five layers of protection:
 *   1. Refuse to operate on malformed input — never try to "fix" a corrupted file.
 *   2. Take a rolling backup at ~/.claude.json.synkro-bak before any write.
 *   3. Validate the post-mutation result is still well-formed JSON and didn't
 *      lose any top-level keys that existed in the original.
 *   4. Atomic write via tmp + fsync + rename(2) (POSIX-atomic).
 *   5. Restore from backup on any error in the write phase.
 *
 * If the file doesn't exist (no claude install on this machine), the mutation
 * is silently skipped — we don't create the file, since claude owns it.
 */
function safelyMutateClaudeJson(mutator: (json: ClaudeJson) => boolean): void {
  if (!existsSync(CLAUDE_JSON_PATH)) {
    // claude.json doesn't exist; nothing to mutate. We don't create it.
    return;
  }

  // (1) Read + parse, refuse to proceed on malformed input.
  const originalText = readFileSync(CLAUDE_JSON_PATH, 'utf-8');
  let parsed: ClaudeJson;
  try {
    parsed = JSON.parse(originalText) as ClaudeJson;
  } catch (err) {
    throw new LocalCCInstallError(
      `refusing to modify malformed ${CLAUDE_JSON_PATH}: ${(err as Error).message}. ` +
      `Please fix the JSON manually before retrying.`,
      err,
    );
  }

  const originalKeys = new Set(Object.keys(parsed));

  // (Mutator returns true if it changed anything; false means no-op.)
  const dirty = mutator(parsed);
  if (!dirty) return;

  // (3a) Validate the in-memory mutation didn't drop a top-level key.
  for (const k of originalKeys) {
    if (!(k in parsed)) {
      throw new LocalCCInstallError(
        `refusing to write ${CLAUDE_JSON_PATH}: mutator dropped top-level key "${k}". ` +
        `This is a bug — please report.`,
      );
    }
  }

  const newText = JSON.stringify(parsed, null, 2) + '\n';

  // (3b) Round-trip parse to guarantee well-formed output before any write.
  try {
    JSON.parse(newText);
  } catch (err) {
    throw new LocalCCInstallError(
      `refusing to write ${CLAUDE_JSON_PATH}: serialized result is not valid JSON. ` +
      `This is a bug — please report.`,
      err,
    );
  }

  // (2) Take a rolling backup of the *original* (pre-mutation) bytes.
  copyFileSync(CLAUDE_JSON_PATH, CLAUDE_JSON_BACKUP_PATH);

  // (4) Atomic write: tmp + fsync + rename. This guarantees a reader
  //     never sees a half-written file — they see either the old or new.
  const tmpPath = `${CLAUDE_JSON_PATH}.synkro-tmp.${process.pid}`;
  try {
    writeFileSync(tmpPath, newText, 'utf-8');
    // fsync the tmp file so the bytes are durably on disk before rename.
    const fd = openSync(tmpPath, 'r');
    try { fsyncSync(fd); } finally { closeSync(fd); }
    renameSync(tmpPath, CLAUDE_JSON_PATH);
  } catch (err) {
    // (5) Best-effort recovery: restore the original from backup.
    try { unlinkSync(tmpPath); } catch { /* ignore */ }
    try { copyFileSync(CLAUDE_JSON_BACKUP_PATH, CLAUDE_JSON_PATH); } catch { /* ignore */ }
    throw new LocalCCInstallError(
      `failed to write ${CLAUDE_JSON_PATH}: ${(err as Error).message}. ` +
      `Backup at ${CLAUDE_JSON_BACKUP_PATH} preserves the prior state.`,
      err,
    );
  }
}

/**
 * Register the synkro-local MCP server at PROJECT scope (.mcp.json inside
 * ~/.synkro/cc_sessions) — NOT at user scope in ~/.claude.json. User scope
 * would make every claude session on the machine spawn the plugin and race
 * to bind the UDS, which corrupts request routing.
 */
function writeProjectMcpJson(): void {
  for (const c of CHANNELS) {
    const mcp = {
      mcpServers: {
        [MCP_SERVER_NAME]: {
          command: 'bun',
          args: [c.pluginPath],
        },
      },
    };
    writeFileSync(c.projectMcpPath, JSON.stringify(mcp, null, 2) + '\n', 'utf-8');
  }
}

/**
 * Pre-accept the workspace trust dialog and approve the project-scoped MCP
 * server in ~/.claude.json so claude doesn't block on those prompts under
 * tmux. Also strips any stale top-level mcpServers["synkro-local"] left over
 * from previous (incorrect) installs.
 *
 * All mutations go through safelyMutateClaudeJson — see that function for
 * the integrity guarantees.
 */
function patchClaudeJson(): void {
  safelyMutateClaudeJson(parsed => {
    let dirty = false;

    // Remove any stale user-scope registration from previous installs.
    if (parsed.mcpServers && typeof parsed.mcpServers === 'object' && parsed.mcpServers[MCP_SERVER_NAME]) {
      delete parsed.mcpServers[MCP_SERVER_NAME];
      dirty = true;
    }

    if (!parsed.projects || typeof parsed.projects !== 'object') {
      parsed.projects = {};
    }
    const projects = parsed.projects as Record<string, Record<string, unknown>>;

    for (const dir of CHANNELS.map(c => c.sessionDir)) {
      const existing = projects[dir] && typeof projects[dir] === 'object'
        ? projects[dir]
        : {};
      const wantEnabled = Array.from(new Set([
        ...((existing.enabledMcpjsonServers as string[] | undefined) ?? []),
        MCP_SERVER_NAME,
      ]));
      const next = {
        ...existing,
        hasTrustDialogAccepted: true,
        hasCompletedProjectOnboarding: true,
        enabledMcpjsonServers: wantEnabled,
      };
      if (
        existing.hasTrustDialogAccepted !== true ||
        existing.hasCompletedProjectOnboarding !== true ||
        JSON.stringify(existing.enabledMcpjsonServers ?? []) !== JSON.stringify(wantEnabled)
      ) {
        projects[dir] = next;
        dirty = true;
      }
    }
    return dirty;
  });
}

/**
 * Run the full local-CC install. Throws LocalCCInstallError on any failure
 * the caller is expected to surface. Does NOT start the pueue task — call
 * `pueue.ensureRunning()` after for that.
 */
export function installLocalCC(): { sessionDir: string; pluginPath: string } {
  // bun is required for the plugin runtime — auto-install if missing.
  let bunCheck = spawnSync('bun', ['--version'], { encoding: 'utf-8' });
  if (bunCheck.status !== 0) {
    if (process.platform === 'darwin') {
      console.log('  Installing bun via brew...');
      const brewR = spawnSync('brew', ['install', 'oven-sh/bun/bun'], { encoding: 'utf-8', stdio: 'inherit', timeout: 120_000 });
      if (brewR.status !== 0) {
        throw new LocalCCInstallError('bun auto-install failed. Install manually: curl -fsSL https://bun.sh/install | bash');
      }
      bunCheck = spawnSync('bun', ['--version'], { encoding: 'utf-8' });
      if (bunCheck.status !== 0) {
        throw new LocalCCInstallError('bun installed but not found on PATH. Restart your terminal and re-run install.');
      }
    } else {
      throw new LocalCCInstallError('bun is required. Install it: curl -fsSL https://bun.sh/install | bash');
    }
  }

  writePluginFiles();
  runBunInstall();
  writeProjectMcpJson();
  patchClaudeJson();

  return { sessionDir: SESSION_DIR, pluginPath: PLUGIN_PATH };
}

/**
 * Strip every trace of local-cc from ~/.claude.json:
 *   - user-scope mcpServers["synkro-local"] (legacy from older installs)
 *   - projects[SESSION_DIR] entry (workspace trust + enabledMcpjsonServers we added)
 *
 * Goes through safelyMutateClaudeJson for the same integrity guarantees as
 * patchClaudeJson — atomic write, backup, post-write validation. Errors are
 * surfaced so the caller sees them, not silently swallowed.
 *
 * Plugin files in ~/.synkro/cc_sessions/ are removed by the caller via
 * `rm -rf ~/.synkro` when uninstalling with --purge.
 */
export function uninstallLocalCC(): void {
  safelyMutateClaudeJson(parsed => {
    let dirty = false;
    if (parsed.mcpServers && parsed.mcpServers[MCP_SERVER_NAME]) {
      delete parsed.mcpServers[MCP_SERVER_NAME];
      dirty = true;
    }
    for (const dir of CHANNELS.map(c => c.sessionDir)) {
      if (parsed.projects && typeof parsed.projects === 'object' && parsed.projects[dir]) {
        delete parsed.projects[dir];
        dirty = true;
      }
    }
    return dirty;
  });
}
